import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { supabase, User } from '../lib/supabase';

interface AuthContextType {
  user: SupabaseUser | null;
  userProfile: User | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name: string, phone?: string) => Promise<{ error: Error | null; data: SupabaseUser | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cache para el perfil de usuario
let profileCache: { email: string; profile: User | null; timestamp: number } | null = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos - aumentado para mayor estabilidad

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Fetch del perfil con caché mejorado
  const fetchUserProfile = useCallback(async (email: string, forceRefresh = false): Promise<User | null> => {
    // Verificar caché primero
    if (!forceRefresh && profileCache && profileCache.email === email) {
      const isValid = Date.now() - profileCache.timestamp < CACHE_DURATION;
      if (isValid && profileCache.profile) {
        console.log('✅ Usando perfil desde caché');
        setUserProfile(profileCache.profile);
        return profileCache.profile;
      }
    }

    try {
      setProfileLoading(true);
      console.log('🔍 Cargando perfil para:', email);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (error) {
        console.error('❌ Error al cargar perfil:', error);
        // NO cerrar sesión por error de perfil
        return profileCache?.profile || null;
      }

      if (!data) {
        console.warn('⚠️ Perfil no encontrado para:', email);
        // Mantener perfil en caché si existe
        return profileCache?.profile || null;
      }

      console.log('✅ Perfil cargado correctamente');

      // Actualizar caché
      profileCache = {
        email,
        profile: data,
        timestamp: Date.now(),
      };

      setUserProfile(data);

      // Actualizar last_login en segundo plano
      supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.id)
        .then(() => {})
        .catch(() => {});

      return data;
    } catch (err) {
      console.error('Error inesperado al cargar perfil:', err);
      // Mantener perfil en caché si existe
      return profileCache?.profile || null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.email) {
      await fetchUserProfile(user.email, true);
    }
  }, [user?.email, fetchUserProfile]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const initializeAuth = async () => {
      try {
        console.log('🚀 Inicializando autenticación...');
        
        // Timeout de seguridad
        timeoutId = setTimeout(() => {
          if (mounted && loading) {
            console.warn('⏱️ Timeout alcanzado, continuando sin perfil');
            setLoading(false);
            setProfileLoading(false);
          }
        }, 8000);

        // Obtener sesión actual
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (error) {
          console.error('❌ Error obteniendo sesión:', error);
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        if (!session) {
          console.log('ℹ️ No hay sesión activa');
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        console.log('✅ Sesión activa encontrada');
        setSession(session);
        setUser(session.user);
        
        // Cargar perfil sin bloquear
        if (session.user?.email) {
          fetchUserProfile(session.user.email).catch(() => {
            console.warn('⚠️ Error al cargar perfil, pero sesión se mantiene');
          });
        }
        
        setLoading(false);
        clearTimeout(timeoutId);
      } catch (err) {
        console.error('❌ Error inicializando:', err);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log('🔄 Evento de autenticación:', event);

      // Solo actualizar estado de sesión y usuario
      setSession(session);
      setUser(session?.user ?? null);
      
      // Cargar perfil solo en eventos específicos
      if (event === 'SIGNED_IN' && session?.user?.email) {
        console.log('✅ Usuario inició sesión, cargando perfil...');
        fetchUserProfile(session.user.email, true).catch(() => {
          console.warn('⚠️ Error al cargar perfil después de login');
        });
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 Usuario cerró sesión');
        setUserProfile(null);
        profileCache = null;
      }
      
      setLoading(false);
    });

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  const signIn = async (email: string, password: string) => {
    try {
      // Intentar iniciar sesión
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error };
      }

      // Verificar estado del usuario
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id, email, status')
        .eq('email', email)
        .maybeSingle();

      if (checkError) {
        console.error('Error verificando usuario:', checkError);
      }

      if (!existingUser) {
        console.warn('Usuario autenticado pero no encontrado en tabla users');
      }

      // Verificar si está activo
      if (existingUser && existingUser.status === 'Inactivo') {
        await supabase.auth.signOut();
        return { 
          error: new Error('Tu cuenta está inactiva. Contacta al administrador.') 
        };
      }

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string, name: string, phone?: string) => {
    try {
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (existingUser) {
        return { 
          error: new Error('Este correo electrónico ya está registrado'), 
          data: null 
        };
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            phone,
          },
        },
      });

      if (error) {
        return { error, data: null };
      }

      if (data.user) {
        const { error: profileError } = await supabase.from('users').insert({
          id: data.user.id,
          email,
          name,
          phone: phone || null,
          role: 'Funcionario',
          status: 'Activo',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (profileError) {
          console.error('Error creating user profile:', profileError);
        }
      }

      return { error: null, data: data.user };
    } catch (err) {
      return { error: err as Error, data: null };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    } finally {
      setUser(null);
      setUserProfile(null);
      setSession(null);
      profileCache = null;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Usar __BASE_PATH__ para construir la URL correcta incluso con subpaths
      const basePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/';
      const redirectUrl = `${window.location.origin}${basePath.replace(/\/$/, '')}/login`;

      // Log para debugging
      console.log('🔐 Enviando enlace de recuperación a:', redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      return { error };
    } catch (err) {
      return { error: err as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        userProfile, 
        session, 
        loading, 
        profileLoading,
        signIn, 
        signUp, 
        signOut, 
        resetPassword,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
